import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { alerta } from '../components/shared/alertaStore';
import { PREFERENCIAS_QUERY_KEY } from './useNotificacoes';
import pushService from '../services/pushService';
import { base64urlParaUint8Array } from '../utils/base64url';
import {
  inscricaoComChaveAntiga,
  permissoesDeNotificacao,
  precisaInstalarNoIos,
  pushSuportado,
} from '../utils/push';
import { obterRegistroServiceWorker, registrarServiceWorker } from '../utils/serviceWorker';

export const PUSH_STATUS_QUERY_KEY = ['push', 'status'];

const paraJSON = (subscription) =>
  typeof subscription?.toJSON === 'function' ? subscription.toJSON() : subscription;

const mensagemErro = (error) =>
  error?.response?.data?.mensagem ||
  error?.message ||
  'Não foi possível configurar as notificações neste dispositivo.';

const MENSAGENS_NAO_CONFIGURADO = {
  ausente: 'As notificações push ainda não estão configuradas no servidor.',
  'chave-invalida':
    'As chaves de Web Push do servidor estão inválidas. Peça ao administrador para revisar a configuração.',
};

/** Texto amigável para o motivo devolvido pelo backend (`ausente`/`chave-invalida`). */
export const textoNaoConfigurado = (motivo) =>
  MENSAGENS_NAO_CONFIGURADO[motivo] || MENSAGENS_NAO_CONFIGURADO.ausente;

/**
 * Estado e ações das notificações Web Push.
 *
 * - `ativar()` sempre roda dentro de um clique (exigência dos navegadores para
 *   pedir permissão);
 * - o servidor é checado antes do prompt: sem VAPID configurado nenhuma
 *   permissão é solicitada, e inscrições criadas com outra chave são renovadas;
 * - a inscrição local é reconciliada com o backend ao abrir o app, restaurando
 *   o vínculo após limpeza de dados/rotação de endpoint pelo navegador;
 * - `desativar()` remove todas as inscrições da conta e desliga o opt-in.
 */
export const usePushNotifications = () => {
  const queryClient = useQueryClient();
  const suportado = useMemo(() => pushSuportado(), []);
  const [permissao, setPermissao] = useState(() => permissoesDeNotificacao());
  const [inscricaoLocal, setInscricaoLocal] = useState(null);
  const [carregandoLocal, setCarregandoLocal] = useState(suportado);
  const [isPending, setIsPending] = useState(false);

  const statusQuery = useQuery({
    queryKey: PUSH_STATUS_QUERY_KEY,
    queryFn: pushService.status,
    enabled: suportado,
    staleTime: 30 * 1000,
    retry: 0,
  });

  const { refetch: recarregarStatus } = statusQuery;

  const sincronizarEstadoLocal = useCallback(async () => {
    if (!suportado) {
      setInscricaoLocal(null);
      setCarregandoLocal(false);
      return null;
    }

    try {
      const registro = await obterRegistroServiceWorker();
      const subscription = registro ? await registro.pushManager.getSubscription() : null;
      setInscricaoLocal(subscription || null);
      return subscription || null;
    } catch {
      setInscricaoLocal(null);
      return null;
    } finally {
      setCarregandoLocal(false);
      setPermissao(permissoesDeNotificacao());
    }
  }, [suportado]);

  useEffect(() => {
    sincronizarEstadoLocal();
  }, [sincronizarEstadoLocal]);

  useEffect(() => {
    if (!suportado) return undefined;

    const atualizarPermissao = () => setPermissao(permissoesDeNotificacao());

    window.addEventListener('focus', atualizarPermissao);
    document.addEventListener('visibilitychange', atualizarPermissao);

    return () => {
      window.removeEventListener('focus', atualizarPermissao);
      document.removeEventListener('visibilitychange', atualizarPermissao);
    };
  }, [suportado]);

  const pushAtivo = statusQuery.data?.pushAtivo === true;
  const configurado = statusQuery.data?.configurado ?? null;
  const motivoConfiguracao = statusQuery.data?.motivo ?? null;

  // Auto-cura: se o navegador trocou o endpoint (pushsubscriptionchange) ou o
  // backend perdeu a inscrição, reenvia a assinatura atual sem pedir permissão.
  useEffect(() => {
    if (!suportado || !inscricaoLocal || !pushAtivo || configurado === false) return undefined;

    let ativo = true;

    pushService
      .inscrever(paraJSON(inscricaoLocal))
      .then(() => {
        if (ativo) {
          queryClient.invalidateQueries({ queryKey: PUSH_STATUS_QUERY_KEY });
        }
      })
      .catch(() => {});

    return () => {
      ativo = false;
    };
  }, [suportado, inscricaoLocal, pushAtivo, configurado, queryClient]);

  const ativar = useCallback(async () => {
    if (!suportado) {
      alerta.warning('Este navegador não oferece suporte a notificações push.');
      return { ok: false, motivo: 'nao-suportado' };
    }

    if (precisaInstalarNoIos()) {
      alerta.info(
        'No iPhone, adicione o site à Tela de Início (Compartilhar → Adicionar à Tela de Início) para ativar as notificações.'
      );
      return { ok: false, motivo: 'instalar' };
    }

    // Servidor sem chaves VAPID (ou com chaves inválidas): não faz sentido
    // pedir permissão ao usuário para uma inscrição que não poderia ser usada.
    if (configurado === false) {
      alerta.warning(
        `${textoNaoConfigurado(motivoConfiguracao)} Nenhuma permissão foi solicitada.`
      );
      return { ok: false, motivo: 'nao-configurado' };
    }

    setIsPending(true);

    try {
      // A permissão é pedida logo no clique (exigência do navegador); o estado
      // do servidor vem do status já carregado, sem `await` antes do prompt.
      const permissaoAtual = await window.Notification.requestPermission();
      setPermissao(permissaoAtual);

      if (permissaoAtual !== 'granted') {
        alerta.warning(
          'Permissão de notificações não concedida. Libere nas configurações do navegador para este site.'
        );
        return { ok: false, motivo: 'permissao' };
      }

      const {
        configurado: servidorConfigurado,
        publicKey,
        motivo,
      } = await pushService.vapidPublicKey();

      if (!servidorConfigurado || !publicKey) {
        queryClient.invalidateQueries({ queryKey: PUSH_STATUS_QUERY_KEY });
        alerta.warning(
          `${textoNaoConfigurado(motivo)} A permissão do navegador já foi concedida e será aproveitada assim que o servidor estiver pronto.`
        );
        return { ok: false, motivo: 'nao-configurado' };
      }

      const registro = await obterRegistroServiceWorker();

      if (!registro?.pushManager) {
        throw new Error('O service worker de notificações não está disponível.');
      }

      let subscription = await registro.pushManager.getSubscription();

      // Inscrição criada com outra chave VAPID: o serviço de push responde 403
      // e ela nunca entregaria nada — refaz com a chave atual do servidor.
      if (subscription && inscricaoComChaveAntiga(subscription, publicKey)) {
        await subscription.unsubscribe().catch(() => {});
        subscription = null;
      }

      if (!subscription) {
        subscription = await registro.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64urlParaUint8Array(publicKey),
        });
      }

      await pushService.inscrever(paraJSON(subscription));

      setInscricaoLocal(subscription);
      queryClient.invalidateQueries({ queryKey: PUSH_STATUS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PREFERENCIAS_QUERY_KEY });
      alerta.success('Notificações ativadas neste dispositivo.');

      return { ok: true };
    } catch (error) {
      alerta.error(mensagemErro(error));
      return { ok: false, motivo: 'erro', error };
    } finally {
      setIsPending(false);
    }
  }, [suportado, configurado, motivoConfiguracao, queryClient]);

  const desativar = useCallback(async () => {
    setIsPending(true);

    try {
      const registro = await registrarServiceWorker();
      const subscription = await registro?.pushManager?.getSubscription();

      if (subscription) await subscription.unsubscribe();

      await pushService.removerTodas();

      setInscricaoLocal(null);
      queryClient.invalidateQueries({ queryKey: PUSH_STATUS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PREFERENCIAS_QUERY_KEY });
      alerta.success('Notificações desativadas.');

      return { ok: true };
    } catch (error) {
      alerta.error(mensagemErro(error));
      return { ok: false, motivo: 'erro', error };
    } finally {
      setIsPending(false);
    }
  }, [queryClient]);

  const testar = useCallback(async () => {
    setIsPending(true);

    try {
      const resultado = await pushService.testar();

      if ((resultado?.enviados || 0) > 0) {
        alerta.success(`Notificação de teste enviada para ${resultado.enviados} dispositivo(s).`);
      } else {
        alerta.warning(
          'Nenhum dispositivo recebeu a notificação. Verifique a permissão do navegador.'
        );
      }

      return resultado;
    } catch (error) {
      alerta.error(mensagemErro(error));
      return null;
    } finally {
      setIsPending(false);
    }
  }, []);

  const recarregar = useCallback(async () => {
    await recarregarStatus();
    await sincronizarEstadoLocal();
  }, [recarregarStatus, sincronizarEstadoLocal]);

  return {
    suportado,
    precisaInstalar: precisaInstalarNoIos(),
    permissao,
    configurado,
    motivoConfiguracao,
    pushAtivo,
    dispositivos: statusQuery.data?.dispositivos || [],
    ativo: permissao === 'granted' && Boolean(inscricaoLocal) && pushAtivo && configurado !== false,
    isLoading: statusQuery.isLoading || carregandoLocal,
    isPending,
    error: statusQuery.error,
    ativar,
    desativar,
    testar,
    recarregar,
  };
};

export default usePushNotifications;
